import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_MODAL_WIDTH = 600;
const DEFAULT_MODAL_HEIGHT = 500;

const useDraggableModal = (initialPosition) => {
  const [modalPosition, setModalPosition] = useState(initialPosition || { left: 0, top: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  const centerModal = useCallback(() => {
    if (typeof window === 'undefined') return;

    const modalWidth = modalRef.current?.offsetWidth || DEFAULT_MODAL_WIDTH;
    const modalHeight = modalRef.current?.offsetHeight || DEFAULT_MODAL_HEIGHT;

    const left = Math.max(0, (window.innerWidth - modalWidth) / 2);
    const top = Math.max(0, (window.innerHeight - modalHeight) / 2);

    setModalPosition({ left, top });
  }, []);

  useEffect(() => {
    if (!initialPosition) {
      centerModal();
      return;
    }
    setModalPosition(initialPosition);
  }, [initialPosition, centerModal]);

  useEffect(() => {
    if (initialPosition) return undefined;
    let frame;
    if (typeof window !== 'undefined') {
      frame = window.requestAnimationFrame(() => centerModal());
    }
    return () => {
      if (frame && typeof window !== 'undefined') {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [centerModal, initialPosition]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      if (!isDragging && modalRef.current) {
        centerModal();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [centerModal, isDragging]);

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();

      const isDragHandleClick = e.target.closest('.drag-handle');

      if (isDragHandleClick) {
        e.stopPropagation();
        setIsDragging(true);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('modalDragStart', { detail: { lockSelection: true } }));
        }

        setDragOffset({
          x: e.clientX - (modalPosition.left || 0),
          y: e.clientY - (modalPosition.top || 0)
        });
      } else {
        e.stopPropagation();
      }
    },
    [modalPosition]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof window === 'undefined') return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      const modalWidth = modalRef.current?.offsetWidth || DEFAULT_MODAL_WIDTH;
      const modalHeight = modalRef.current?.offsetHeight || DEFAULT_MODAL_HEIGHT;

      const maxX = window.innerWidth - modalWidth;
      const maxY = window.innerHeight - modalHeight;

      setModalPosition({
        left: Math.max(0, Math.min(newX, maxX)),
        top: Math.max(0, Math.min(newY, maxY))
      });
    },
    [dragOffset, isDragging]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('modalDragEnd', { detail: { unlockSelection: true } }));
    }
  }, [isDragging]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return undefined;

    if (!isDragging) {
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.mozUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.pointerEvents = '';
      return undefined;
    }

    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.mozUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    document.body.style.pointerEvents = 'none';

    if (modalRef.current) {
      modalRef.current.style.pointerEvents = 'auto';
    }

    const handleMove = (event) => handleMouseMove(event);
    const handleUp = () => handleMouseUp();

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);

    return () => {
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.mozUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.pointerEvents = '';
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [handleMouseMove, handleMouseUp, isDragging]);

  return { modalRef, modalPosition, isDragging, handleMouseDown };
};

export default useDraggableModal;
